import type { RumInitialLoadPayload, RumMilestone, RumNavigationTiming, RumContext, RumSentReason } from './types';

/**
 * Structural slice of TanStack Router — lets tests pass a stub and avoids
 * importing the app router (import cycle: router → routes → pages → services).
 */
export type RumRouter = {
  state: { matches: ReadonlyArray<{ routeId: string }> };
  subscribe: (event: 'onResolved', callback: () => void) => () => void;
};

export type RumSessionOptions = {
  send: (payload: RumInitialLoadPayload) => void;
  getContext: () => RumContext;
  /** Flush after this long with no new milestone. */
  quietMs?: number;
  /** Always flush by this long after install, settled or not. */
  hardCapMs?: number;
};

export type RumSession = {
  /** Wire timers, router subscription, and tab-hide flushing. Call once, before first render. */
  install: (router: RumRouter) => void;
  /** Record a milestone at the current time. Write-once; repeats are ignored. */
  recordMilestone: (milestone: RumMilestone) => void;
  /**
   * Declare that a milestone is coming: the quiet-period flush waits for it
   * (hard cap and tab-hide still flush regardless). Lets an embedded app
   * announce early in boot that it will report interactivity, so the beacon
   * isn't flushed before the report — while builds that never announce keep
   * the normal flush timing (version skew between shell and app deploys).
   */
  expectMilestone: (milestone: RumMilestone) => void;
  hasSent: () => boolean;
};

const DEFAULT_QUIET_MS = 5_000;
const DEFAULT_HARD_CAP_MS = 60_000;

/**
 * One RUM session per page load, one beacon per session. Always sends —
 * quiet-period debounce, hard cap, or tab hidden — with whatever milestones
 * were reached, so abandoned loads and coverage gaps show up as missing
 * milestones instead of dropped sessions (unlike the legacy
 * `PerformanceProvider`, which only reported when every slot was filled).
 */
export function createRumSession(options: RumSessionOptions): RumSession {
  const { send, getContext, quietMs = DEFAULT_QUIET_MS, hardCapMs = DEFAULT_HARD_CAP_MS } = options;

  const milestones: Partial<Record<RumMilestone, number>> = {};
  const expectedMilestones = new Set<RumMilestone>();
  let installed = false;
  let sent = false;
  let router: RumRouter | null = null;
  let quietTimer: ReturnType<typeof setTimeout> | null = null;
  let hardCapTimer: ReturnType<typeof setTimeout> | null = null;
  let unsubscribeRouter: (() => void) | null = null;

  const sessionId = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

  // Captured when the first navigation settles: the flush can be seconds
  // later (quiet window / child-app hold), and the user may have navigated
  // on — reading the router live at flush would label the load with the
  // wrong route.
  let settledRoute: string | null = null;

  function liveRoutePattern(): string | null {
    const matches = router?.state.matches;
    return matches?.[matches.length - 1]?.routeId ?? null;
  }

  function getRoutePattern(): string {
    // No pathname fallback: raw paths carry ids and become metric labels.
    return settledRoute ?? liveRoutePattern() ?? 'unknown';
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      flush('hidden');
    }
  }

  function cleanup() {
    if (quietTimer != null) clearTimeout(quietTimer);
    if (hardCapTimer != null) clearTimeout(hardCapTimer);
    quietTimer = null;
    hardCapTimer = null;
    unsubscribeRouter?.();
    unsubscribeRouter = null;
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  function flush(reason: RumSentReason) {
    if (sent) {
      return;
    }
    sent = true;
    cleanup();

    // Telemetry must never break the app.
    try {
      send({
        v: 1,
        kind: 'initial_load',
        session_id: sessionId,
        ...getContext(),
        route: getRoutePattern(),
        milestones: withPageReady(milestones),
        navigation: getNavigationTiming(),
        sent_reason: reason,
      });
    } catch {
      // swallow
    }
  }

  function onQuietElapsed() {
    // A child-app load in flight is an expected long quiet stretch; flushing
    // now would report slow loads as "render never happened" and truncate the
    // histogram's tail. Same for milestones an embedded app announced via
    // expectMilestone — but announcements only hold once a child app actually
    // started loading: the shell *preloads* app bundles on page loads that
    // never boot them, and a module-eval announcement from a preloaded-only
    // bundle must not stall beacons on pages that never run the app.
    const childAppStarted = milestones.child_app_load_start != null;
    const childAppInFlight = childAppStarted && milestones.child_app_rendered == null;
    const awaitingExpected = childAppStarted && [...expectedMilestones].some((m) => milestones[m] == null);
    if (childAppInFlight || awaitingExpected) {
      resetQuietTimer();
      return;
    }
    flush('quiet');
  }

  function resetQuietTimer() {
    if (!installed || sent) {
      return;
    }
    if (quietTimer != null) {
      clearTimeout(quietTimer);
    }
    quietTimer = setTimeout(onQuietElapsed, quietMs);
  }

  return {
    install(appRouter) {
      if (installed) {
        return;
      }
      installed = true;
      router = appRouter;

      unsubscribeRouter = appRouter.subscribe('onResolved', () => {
        settledRoute = liveRoutePattern();
        recordMilestoneInternal('first_route_settled');
        unsubscribeRouter?.();
        unsubscribeRouter = null;
      });

      document.addEventListener('visibilitychange', onVisibilityChange);
      hardCapTimer = setTimeout(() => flush('timeout'), hardCapMs);
      resetQuietTimer();
    },
    recordMilestone(milestone) {
      recordMilestoneInternal(milestone);
    },
    expectMilestone(milestone) {
      if (sent) {
        return;
      }
      expectedMilestones.add(milestone);
    },
    hasSent: () => sent,
  };

  function recordMilestoneInternal(milestone: RumMilestone) {
    // Records after the flush are dropped on purpose: this beacon measures
    // the initial load only. A child app booted by a later in-session
    // navigation is not part of the initial load — it belongs to transition
    // measurement (a future, separate beacon kind), and folding it into a
    // beacon labeled with the entry route would corrupt the histogram.
    if (sent || milestones[milestone] != null) {
      return;
    }
    milestones[milestone] = performance.now();
    resetQuietTimer();
  }
}

/**
 * Copies the recorded milestones and adds the synthetic `page_ready` one: the
 * strongest "page became usable" signal this load produced. When a child app
 * started loading, it owns readiness — falling back past a missing
 * `child_app_rendered` would report an abandoned app load as "ready" at the
 * (much earlier) route settle. Absent means the page never became usable,
 * consistent with every other milestone.
 */
function withPageReady(milestones: Partial<Record<RumMilestone, number>>) {
  const pageReady =
    milestones.child_app_load_start != null
      ? (milestones.child_app_interactive ?? milestones.child_app_rendered)
      : milestones.first_route_settled;
  if (pageReady == null) {
    return { ...milestones };
  }
  return { ...milestones, page_ready: pageReady };
}

function getNavigationTiming(): RumNavigationTiming {
  if (typeof performance.getEntriesByType !== 'function') {
    return {};
  }
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  if (entry == null) {
    return {};
  }
  const timing: RumNavigationTiming = {};
  // 0 = hasn't happened yet; omit rather than report a fake fast value.
  if (entry.responseStart > 0) timing.ttfb = entry.responseStart;
  if (entry.domContentLoadedEventEnd > 0) timing.dom_content_loaded = entry.domContentLoadedEventEnd;
  if (entry.loadEventEnd > 0) timing.load_event = entry.loadEventEnd;
  return timing;
}
